import { t } from "i18next";
import { invert, isNumber, isObject } from "lodash";
import i18n from "../../../../../i18n";
import { SourceElementData } from "../objects/sourceElementData";
import { FilterSortItem } from "../pageBuilderSourceFilterSortModal";
import { CircuitOffer } from "../api/packagesApi";

export class CircuitOffersSourceData implements SourceElementData {
    constructor(
        private locale: number,
        private currencyIsoCode: string | undefined,
        private offer: CircuitOffer
    ) { }

    public getFieldValue(field: string): string | null {
        switch (field) {
            case 'id': {
                return this.offer.id.toString();
            }
            case 'name': return this.offer.circuit?.localization?.find((item) => {
                return item.locale === this.locale
            })?.name ??
                this.offer.circuit?.name ??
                '';
            case 'picture': {
                if (this.offer.picture) {
                    return this.offer.picture.url ??
                        this.offer.picture.thumbnail_big ??
                        this.offer.picture.thumbnail_medium ??
                        this.offer.picture.thumbnail_little ??
                        '';
                }
                const picture = this.offer.circuit?.pictures[0];
                return picture?.url ??
                    picture?.thumbnail_big ??
                    picture?.thumbnail_medium ??
                    picture?.thumbnail_little ??
                    '';
            }
            case 'link': {
                const lowestPrice = isNumber(this.offer.circuit?.lowest_price) ?
                    this.offer.circuit.lowest_price :
                    (
                        isObject(this.offer.circuit?.lowest_price) ?
                            Math.min(...Object.values(this.offer.circuit.lowest_price)) :
                            0
                    );
                const lowestPriceVersion = invert(
                    isObject(this.offer.circuit?.lowest_price) ?
                        this.offer.circuit?.lowest_price ?? {} :
                        {}
                )[lowestPrice];
                const name = this.offer.circuit?.localization?.find((item) => {
                    return item.locale === this.locale
                })?.name ??
                    this.offer.circuit?.name ??
                    ''
                const nameInUrl = name.replace(/ /g, '-').replace(/\//g, '');
                return lowestPriceVersion ?
                    `/:urlName/circuits-details/${nameInUrl}/${this.offer.circuit?.id}` + '/' + lowestPriceVersion :
                    `/:urlName/circuits-details/${nameInUrl}/${this.offer.circuit?.id}`
            };
            case 'file': {
                return JSON.stringify(this.offer.file);
            }
            case 'location': {
                return this.offer.circuit?.destination?.name ??
                    this.offer.circuit?.destination?.international_name ??
                    '';
            }
            case 'title': {
                return this.offer.localization?.find((item) => {
                    return item.locale === this.locale;
                })?.title ??
                    this.offer.title;
            }
            case 'type': {
                return [
                    this.offer.circuit?.type &&
                    t(`shared.${this.offer.circuit.type.toLowerCase()}`),
                    `${this.offer.circuit?.duration_day}J/${this.offer.circuit?.duration_night}N`
                ].filter((item) => !!item).join(' - ')
            }
            case 'price': {
                const manualPrice = parseFloat(this.offer.manual_price ?? '0');
                if (manualPrice > 0) {
                    return new Intl.NumberFormat(
                        i18n.language,
                        {
                            style: 'currency',
                            currency: this.currencyIsoCode ?? 'EUR',
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                        }
                    ).format(manualPrice);
                }

                const price = isNumber(this.offer.circuit?.lowest_price) ?
                    this.offer.circuit.lowest_price :
                    (
                        isObject(this.offer.circuit?.lowest_price) ?
                            Math.min(...Object.values(this.offer.circuit.lowest_price)) :
                            0
                    );
                return new Intl.NumberFormat(
                    i18n.language,
                    {
                        style: 'currency',
                        currency: this.offer.circuit?.trip?.currency?.iso_code ?? 'EUR',
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                    }
                ).format(price);
            }
            case 'discount': {
                return this.offer.percentage;
            }
            case 'stars': {
                return this.offer.hotel_star?.toFixed(0) ?? '0';
            }
            case 'description': {
                return this.offer.localization?.find((item) => {
                    return item.locale === this.locale;
                })?.description ??
                    this.offer.description;
            }
            case 'condition': {
                return this.offer.localization?.find((item) => {
                    return item.locale === this.locale;
                })?.condition ??
                    this.offer.condition;
            }
        }
        return null;
    }

    public getFiterSortItem(): FilterSortItem {
        return {
            id: this.getFieldValue('id') ?? '',
            label: this.getFieldValue('name') ?? '',
            active: true
        };
    }
}